/**
 * Encoding for the load-schedule command body.
 *
 * The API receives minutes past midnight and YYMMDD dates. The server encodes
 * the Chanels HHMM and DDMMYY fields. Getting that wrong is not visible in the console:
 * it shows up months later as a meter shedding load at the wrong hour, so the
 * conversion lives here on its own and is covered by tests.
 */

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** minutesFromTime turns "06:30" into 390. */
export function minutesFromTime(hhmm) {
  const match = TIME_PATTERN.exec(String(hhmm ?? ""));
  if (!match) {
    throw new Error(`"${hhmm}" is not a time of day in 24-hour HH:MM form.`);
  }
  return Number(match[1]) * 60 + Number(match[2]);
}

/** yymmdd turns "2026-08-04" into "260804". */
export function yymmdd(iso) {
  const match = ISO_DATE_PATTERN.exec(String(iso ?? ""));
  if (!match) {
    throw new Error(`"${iso}" is not a date in YYYY-MM-DD form.`);
  }
  return `${match[1].slice(2)}${match[2]}${match[3]}`;
}

/**
 * toScheduleBody converts the form's friendly values into the wire
 * representation, refusing anything the device would reject anyway so the
 * operator is told before the request is sent.
 *
 * A window whose end time is earlier than its start time is allowed: that is
 * how an overnight window such as 22:00 to 06:00 is expressed.
 */
export function toScheduleBody(s) {
  if (!Array.isArray(s.days) || s.days.length !== 7) {
    throw new Error("A schedule needs a setting for each of the seven days.");
  }
  if (!s.days.some(Boolean)) {
    throw new Error("Choose at least one day for this schedule to apply.");
  }

  const startDate = yymmdd(s.startDate);
  const endDate = yymmdd(s.endDate);
  if (endDate < startDate) {
    throw new Error("The end date falls before the start date.");
  }

  const limit = Number(s.limit);
  if (!Number.isInteger(limit) || limit < 0 || limit > 9999) {
    throw new Error("The limit must be a whole number from 0 to 9999.");
  }

  return {
    Days: [...s.days],
    StartTime: minutesFromTime(s.startTime),
    EndTime: minutesFromTime(s.endTime),
    StartDate: startDate,
    EndDate: endDate,
    Limit: limit,
    ReturnLimit: Number(s.returnLimit ?? 0),
    OneTimeAction: Number(s.oneTimeAction ?? 0),
    ConnectMinutes: Number(s.connectMinutes ?? 1),
    DisconnectMinutes: Number(s.disconnectMinutes ?? 1),
    StartState: Number(s.startState ?? 1),
    StopState: Number(s.stopState ?? 0),
  };
}
